import {
  IDiscountRule,
  INormalizedDiscountRuleItem,
} from "@pbm/discount-rules/contracts";
import { IconButton } from "@funcional/react-ui";
import { Private, SsoRolesEnum } from "@funcional/react-auth-ui";
import { IntlDateTime, IntlEnum } from "@funcional/react-intl-ui";
import { Column, useExpanded, useTable } from "react-table";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { ProductsList } from "./ProductsList";
import { DiscountRuleRangesList } from "./DiscountRuleRangesList";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { ReactTable } from "@funcional/react-table-ui";

interface DiscountRulesListProps {
  data: IDiscountRule[];
  programCode: string;
  programFamilyCode: string;
  deleteRecord: Function;
}

export function DiscountRulesList({
  data,
  programCode,
  programFamilyCode,
  deleteRecord,
}: DiscountRulesListProps) {
  const history = useHistory();
  const normalizedData = useMemo(() => {
    const list: INormalizedDiscountRuleItem[] = [];
    (data || []).forEach((item) => {
      list.push({
        key: item.id,
        id: item.id,
        type: item.type,
        monthlyLimit: item.monthlyLimit,
        ranges: item.ranges,
        numberOfRanges: item.ranges?.length || 0,
        createdAt: item.createdAt,
        effectiveSince: item.effectiveSince,
        effectiveUntil: item.effectiveUntil,
        historyType: item.accountedProducts.length > 1 ? "Grupo" : "Individual",
        accountedProducts: item.accountedProducts,
        discountedProducts: item.discountedProducts,
      } as any);
    });
    return list;
  }, [data]);

  const columns: Column<INormalizedDiscountRuleItem>[] = useMemo(
    () =>
      [
        {
          Header: "EAN",
          Cell: ({ row }: any) =>
            row.original.discountedProducts.map(({ ean }: any) => (
              <div className="my-1">{ean}</div>
            )),
        },
        {
          Header: "Nome",
          Cell: ({ row }: any) =>
            row.original.discountedProducts.map(({ name }: any) => (
              <div className="my-1">{name || "-"}</div>
            )),
        },
        {
          Header: "Apresentação",
          Cell: ({ row }: any) =>
            row.original.discountedProducts.map((item: any) => (
              <div className="my-1">{item.package || "-"}</div>
            )),
        },
        {
          Header: "Tipo",
          accessor: "type",
          Cell: ({ value }) => (
            <IntlEnum name="discount_rule_type" value={value} />
          ),
        },
        { Header: "Histórico", accessor: "historyType" },
        {
          Header: "Limite mensal",
          accessor: "monthlyLimit",
          Cell: ({ value }: any) => value + " unidades",
        },
        { Header: "Faixas", accessor: "numberOfRanges" },
        {
          Header: "Início da Vigência",
          accessor: "effectiveSince",
          Cell: ({ value }: any) => <IntlDateTime value={value} />,
        },
        {
          Header: "Fim da Vigência",
          accessor: "effectiveUntil",
          Cell: ({ value }: any) => <IntlDateTime value={value} />,
        },
      ] as Column<INormalizedDiscountRuleItem>[],
    []
  );

  const crudCol = useMemo(
    () => ({
      id: "crud",
      Cell: ({ row }: any) => (
        <div className="flex space-x-2 gap-x-4">
          <Private
            roles={[
              SsoRolesEnum.MASTER,
              SsoRolesEnum.SUPPORT,
              SsoRolesEnum.PHARMA_BENEFIT_MANAGER,
              SsoRolesEnum.RETAIL_PHARMA_BENEFIT_MANAGER,
            ]}
          >
            <IconButton
              title="Editar"
              onClick={() =>
                history.push(
                  `/pbm/programs/${programCode}/families/${programFamilyCode}/discount-rules/${row.original.id}/edit`
                )
              }
            >
              <EditIcon />
            </IconButton>
          </Private>

          <Private
            roles={[
              SsoRolesEnum.MASTER,
              SsoRolesEnum.SUPPORT,
              SsoRolesEnum.PHARMA_BENEFIT_MANAGER,
              SsoRolesEnum.RETAIL_PHARMA_BENEFIT_MANAGER,
            ]}
          >
            <IconButton
              title="Excluir"
              onClick={() => deleteRecord(row, row.original.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Private>
        </div>
      ),
    }),
    [deleteRecord, programCode, programFamilyCode, history]
  );

  const tableInstance = useTable(
    { columns, data: normalizedData as any },
    useExpanded,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...columns, crudCol]);
    }
  );

  return (
    <ReactTable
      tableInstance={tableInstance}
      renderSubComponent={(row: any) => (
        <>
          {row.original.accountedProducts.length > 1 ? (
            <>
              <h6 className="mb-4 font-medium text-gray-500 text-md">
                Produtos Considerados no Histórico de Vendas:
              </h6>
              <ProductsList data={row.original.accountedProducts} />
            </>
          ) : null}
          <h6 className="my-4 font-medium text-gray-500 text-md">
            Faixas de Descontos
          </h6>
          <DiscountRuleRangesList data={row.original.ranges} />
        </>
      )}
    />
  );
}
